import { Alert, Descriptions, Divider, Form, InputNumber, Select, Space } from 'antd';
import { message } from '@/components/Message';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { ID } from '../../global';
import { DateFormat } from '@/models/common';
import { balanceRefundCalc, BalanceRefundInfo, useRefundBalance } from '@/models/user';
import { getPaymentChannelDesc } from '../../pages/Order/helpers';
import AsyncWrapper from '../AsyncWrapper';
import Modal from '../Modal';
import PosButton from '../PosButton';
import Price from '../Price';
import TransactionLogList from '../Transaction/TransactionLogList';
import { Perms } from '@/models/perms';

export type Props = {
  userId: ID;
  onClose: () => void;
};

const UserBalanceRefund: React.FC<Props> = ({ userId, onClose }: Props) => {
  const [, , helpers] = useContext(BaseInfoContext);
  const [balanceRefundInfo, setBalanceRefundInfo] = useState<BalanceRefundInfo>();
  const [balanceRefundInfoErr, setBalanceRefundInfoErr] = useState();
  const [form] = useForm();
  const { payload: refundResult, send: refundBalance, loading, reset } = useRefundBalance();

  const onCancel = () => {
    setBalanceRefundInfo(undefined);
    setBalanceRefundInfoErr(undefined);
    form.resetFields();
    onClose();
    reset();
  };

  useEffect(() => {
    if (userId) {
      balanceRefundCalc(userId)
        .then(v => {
          setBalanceRefundInfo(v);
        })
        .catch(e => {
          setBalanceRefundInfoErr(e);
        });
    } else {
      setBalanceRefundInfo(undefined);
    }
  }, [userId]);

  return (
    <>
      <Modal
        title="退款销户"
        open={!!userId}
        onCancel={onCancel}
        destroyOnHidden
        cancelText="关闭"
        okText={
          helpers.hasPermission(Perms.USER_BALANCE_REFUND) ? '确认退款(双击确认)' : '无操作权限'
        }
        okButtonProps={{
          danger: true,
          type: 'primary',
          loading: loading,
          disabled:
            !balanceRefundInfo ||
            Boolean(refundResult) ||
            !helpers.hasPermission(Perms.USER_BALANCE_REFUND),
          onDoubleClick: async () => {
            const values = form.getFieldsValue();
            console.log(values);
            if (!values.method) {
              message.error('请选择退款类型');
              return;
            }
            const method = values.method;

            if (!values.refundAmount) {
              message.error('请输入退款金额');
            }
            if (method === 3) {
              if (values.balanceAmount === undefined || values.balanceAmount === null) {
                message.error('请输入退款后账户余额');
              }
            }
            values.userId = userId;
            await refundBalance(values);
            message.success('退款成功');
          },
        }}
        onOk={() => {}}>
        <Alert
          type="warning"
          message={'余额退款规则说明'}
          style={{ marginBottom: 16 }}
          description={
            <Space direction="vertical" size={0}>
              <div>
                <b>商家责任</b>: 退款金额 = 账户余额，但不超出累计充值实付金额
              </div>
              <div>
                <b>客户责任</b>: 退款金额 = 累计充值实付金额 - 累计余额消费金额
              </div>
              <div>
                <b>协商退款</b>: 手动填写退款金额与账户余额
              </div>
              <div>
                <b>退款方式</b>:
                退款仅能原路返回，微信、支付宝支付的从线上渠道返回，现金支付请操作完毕后付现给客户
              </div>
            </Space>
          }
        />
        <AsyncWrapper
          render={Boolean(balanceRefundInfo)}
          loading={!Boolean(balanceRefundInfo)}
          error={balanceRefundInfoErr}>
          {() => {
            return (
              <Form
                form={form}
                onValuesChange={field => {
                  if (field.method) {
                    if (field.method === 1) {
                      form.setFieldsValue({
                        refundAmount: balanceRefundInfo.maxMerchantRefundAmount,
                      });
                    } else if (field.method === 2) {
                      form.setFieldsValue({
                        refundAmount: balanceRefundInfo.maxUserRefundAmount,
                      });
                    }
                  }
                }}
                initialValues={{
                  balanceAmount: 0,
                }}>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="余额">
                    <Price value={balanceRefundInfo.balance} />
                  </Descriptions.Item>
                  <Descriptions.Item label="累计充值金额">
                    <Price value={balanceRefundInfo.totalRechargeAmount} />
                  </Descriptions.Item>
                  <Descriptions.Item label="累计消费金额">
                    <Price value={balanceRefundInfo.totalConsumeAmount} />
                  </Descriptions.Item>
                  {balanceRefundInfo.totalRefundedAmount ? (
                    <Descriptions.Item label="累计已退款金额">
                      <Price value={balanceRefundInfo.totalRefundedAmount} />
                    </Descriptions.Item>
                  ) : null}
                  <Descriptions.Item label="退款订单">
                    <Space direction="vertical" size={5}>
                      <div>将从以下订单发起退款</div>
                      {balanceRefundInfo.rechargeTransactionLogs.map(log => {
                        return (
                          <PosButton
                            key={log.id}
                            onClick={() => {
                              helpers.showOrderModal({
                                orderSn: log.orderSn,
                              });
                            }}
                            size="small"
                            icon={<>{getPaymentChannelDesc(log.paymentMethod)}</>}>
                            &nbsp;实付
                            <Price value={log.amount} />
                            ，可退 <Price value={log.availableAmount} />
                            &nbsp;{dayjs(log.addTime).format(DateFormat)}
                          </PosButton>
                        );
                      })}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="退款类型">
                    <Form.Item name="method">
                      <Select
                        placeholder="请选择退款类型"
                        getPopupContainer={trigger => trigger.parentNode}>
                        <Select.Option value={1}>商家责任</Select.Option>
                        <Select.Option value={2}>客户责任</Select.Option>
                        <Select.Option value={3}>协商退款</Select.Option>
                      </Select>
                    </Form.Item>
                  </Descriptions.Item>
                  <Descriptions.Item label="退款金额">
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.method !== cur.method}>
                      {({ getFieldValue }) => {
                        const method = getFieldValue('method');
                        if (method === 1 || method === 2) {
                          return (
                            <Form.Item name="refundAmount">
                              <Price />
                            </Form.Item>
                          );
                        } else if (method === 3) {
                          return (
                            <>
                              <Form.Item label={'退款金额'} name="refundAmount" required>
                                <InputNumber
                                  min={0.01}
                                  max={balanceRefundInfo.maxRefundableAmount}
                                />
                              </Form.Item>
                              <Form.Item label={'退款后余额'} name="balanceAmount" required>
                                <InputNumber min={0} max={balanceRefundInfo.balance} />
                              </Form.Item>
                            </>
                          );
                        } else {
                          return <div>选择退款类型后试算</div>;
                        }
                      }}
                    </Form.Item>
                  </Descriptions.Item>
                </Descriptions>
              </Form>
            );
          }}
        </AsyncWrapper>

        {refundResult && (
          <>
            <Divider />
            <div>
              <Alert message={'退款成功'} showIcon type="success" style={{ marginBottom: 16 }} />
              <TransactionLogList list={refundResult} showAddTime={false} />
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default UserBalanceRefund;
